import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { tableFromIPC } from 'apache-arrow';
import { fromArrow } from 'arquero';
import { Papa } from 'ngx-papaparse';
import { GeneralHelpers } from '../../helpers/general.helper';
import { FileHelpers } from '../../helpers/file.helper';
import { ImageGroupResult, ImageResult, RenderData } from '../../interfaces/table-result.interface';
import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import {
  ERROR_DISPLAY_IMAGE,
  ERROR_DISPLAY_MARKDOWN,
  ERROR_DISPLAY_TABLE,
} from '../../constants/additional-methods.constants';
import { MessageService } from '../message/message.service';
import { FILE_EXTENSIONS, ResponseType, SCROLL_TO_CHUNK } from '../../constants/general.constants';
import { StepService } from '../step/step.service';
import { FileUnifiedService } from '../file-unified/file-unified.service';
import { MarkdownService } from '../markdown/markdown.service';

@Injectable({
  providedIn: 'root',
})
export class RenderService {
  private readonly papa = inject(Papa);
  private readonly messageService = inject(MessageService);
  private readonly stepService = inject(StepService);
  private readonly fileUnifiedService = inject(FileUnifiedService);
  private readonly markdownService = inject(MarkdownService);

  public renderList: RenderData[] = [];
  public renderServiceSubject$ = new Subject<RenderData>();

  public addToRenderList(data: RenderData): void {
    const { value, stepId, type, options, name } = data;
    const renderItem: RenderData = { value, stepId, type, options, name };
    this.renderServiceSubject$.next(renderItem);
  }

  // The public render entry points (renderVideo/renderTable/renderImage/
  // displayImageGroup/renderMarkdown/renderChart/skipToStep/log/output) are
  // invoked from step runtime code with heterogeneous, dynamically-typed
  // argument arrays; `args`/`data` stay `any[]` (inherent, per the audit) and
  // are narrowed with runtime typeof/instanceof checks inside each method.
  public renderVideo(args: any[], context: ChunkContext) {
    if (!Array.isArray(args) || args.length === 0) {
      const error = new Error('Error displaying video');
      context.addMessage(error.message, 'danger');
      throw error;
    }

    const video = args[0];
    const chunkData = context.getChunk();

    if (video === null || video === undefined) {
      const error = new Error('Error displaying video');
      context.addMessage(error.message, 'danger');
      throw error;
    }

    const options = args.length > 1 && args[1] !== null && typeof args[1] === 'object' && !Array.isArray(args[1]) ? args[1] : {};

    // If arrowAlias is set, the value is an S3 key for presigned HLS — pass through directly.
    // The render-video component handles presigning; no local file lookup needed.
    if (typeof video === 'string' && options['arrowAlias']) {
      this.addToRenderList({
        value: video,
        stepId: chunkData.stepId,
        type: 'video',
        options,
      });
      return;
    }

    // If it's a URL (including YouTube), handle it directly
    if (typeof video === 'string' && this.isVideoPath(video)) {
      const youtubeRegex =
        /^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
      if (youtubeRegex.test(video)) {
        this.addToRenderList({
          value: video,
          stepId: chunkData.stepId,
          type: 'youtube',
        });
        return;
      }
      this.addToRenderList({
        value: video,
        stepId: chunkData.stepId,
        type: 'video',
        options,
      });
      return;
    }

    // If it's a byte array string, convert it to a video blob
    if (
      typeof video === 'string' &&
      GeneralHelpers.canBeParsedToNumberArray(video)
    ) {
      const numberArray = video.split(',').map(Number);
      const videoBuffer = new Uint8Array(numberArray);
      const videoBlob = new Blob([videoBuffer], { type: 'video/mp4' });
      const videoUrl = URL.createObjectURL(videoBlob);
      this.addToRenderList({
        value: videoUrl,
        stepId: chunkData.stepId,
        type: 'video',
      });
      return;
    }

    // If it's a string that looks like a filename, try to load it from notebook files
    if (typeof video === 'string') {
      // Try to load the file
      // context.setBusy(true, 'Loading video file');

      this.fileUnifiedService.getFile([video, ResponseType.ArrayBuffer], context)
        .then((fileData) => {
          if (fileData) {
            try {
              // Convert the file data to a blob URL
              const videoBlob = new Blob([fileData], { type: 'video/mp4' });
              const videoUrl = URL.createObjectURL(videoBlob);
              this.addToRenderList({
                value: videoUrl,
                stepId: chunkData.stepId,
                type: 'video',
              });
            } catch (error) {
              context.setChunkResultStatus("error");
              const errorMsg = `Error processing video file: ${GeneralHelpers.errorMessage(error)}`;
              context.addMessage(errorMsg, 'danger');
              throw new Error(errorMsg);
            }
          } else {
            const errorMsg = `Could not load video file: ${video}`;
            context.addMessage(errorMsg, 'danger');
            throw new Error(errorMsg);
          }
        })
        .catch((error) => {
          context.setChunkResultStatus("error");
          context.addMessage(`Error loading video file: ${error.message}`, 'danger');
          throw error;
        })
        .finally(() => {
          // context.setBusy(false);
        });
      return;
    }

    const error = new Error('Invalid video data');
    context.addMessage(error.message, 'danger');
    throw error;
  }

  private isVideoPath(str: string): boolean {
    try {
      new URL(str);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  public async renderTable(args: any[], context: ChunkContext) {
    console.log('RenderService: renderTable called with args:', args);
    const chunkData = context.getChunk();
    let tableData = args[0];
    const options = args[1] || {}; // Second argument contains options like title, sortable, etc.

    console.log('RenderService: chunkData:', chunkData);
    console.log('RenderService: initial tableData:', tableData);
    console.log('RenderService: options:', options);

    if (!chunkData?.stepId || args === null || args === undefined) {
      const error = new Error(ERROR_DISPLAY_TABLE);
      context.addMessage(ERROR_DISPLAY_TABLE, 'danger');
      context.setChunkResultStatus("error");
      throw error;
    }

    try {
      if (typeof tableData === 'string') {
        console.log('RenderService: Processing string data');
        const fileType: string =
          FileHelpers.detectFileTypeFromByteString(tableData);
        console.log('RenderService: Detected file type:', fileType);

        switch (fileType) {
          case 'CSV':
            tableData = await this.processByteStringToCsv(tableData);
            break;
          case 'JSON':
            // If byte-string encoded, convert to actual characters first
            if (GeneralHelpers.canBeParsedToNumberArray(tableData)) {
              const bytes = tableData.split(',').map(Number);
              tableData = new TextDecoder().decode(new Uint8Array(bytes));
            }
            tableData = this.processJsonData(tableData);
            break;
          case 'Arrow':
            tableData = await this.processArrowData(tableData);
            break;
          case 'Parquet':
            // Browser-side Parquet support was removed with parquet-wasm
            // (EHQ-171). Fail with a comprehensible message rather than letting
            // this fall through to the byte-string/CSV default, which would
            // render the raw Parquet bytes as nonsense columns.
            // No "use a headless run" suggestion: enoch has the whole CONVERT
            // family commented out and no Parquet branch at all, so headless
            // never supported this either.
            throw new Error(
              'Parquet data can no longer be rendered in the browser. Convert it upstream to CSV or Arrow first.',
            );
          default:
            if (GeneralHelpers.canBeParsedToNumberArray(tableData)) {
              tableData = await this.processByteStringToCsv(tableData);
            } else {
              throw new Error('Unknown string data type');
            }
        }
      } else if (typeof tableData === 'object') {
        console.log('RenderService: Processing object data');
        if (GeneralHelpers.isArrowTable(tableData)) {
          console.log('RenderService: Arrow table detected');
          tableData = await this.processArrowTable(tableData);
        } else {
          console.log('RenderService: Regular object data');
          tableData = this.processObjectData(tableData);
        }
      } else {
        console.log('RenderService: Unsupported data type:', typeof tableData);
        throw new Error('Unsupported data type');
      }

      console.log('RenderService: Processed tableData:', tableData);
      const formattedData = this.formatDataForTableComponent(tableData);

      // Create render data with optional custom title
      const renderData: RenderData = {
        value: formattedData,
        stepId: chunkData.stepId,
        type: 'table',
      };

      // Add custom title if provided in options
      if (options.title) {
        renderData.name = options.title;
      }

      console.log('RenderService: Adding to render list:', renderData);
      this.addToRenderList(renderData);
    } catch (error) {
      console.error('RenderService: Error in renderTable:', error);
      context.addMessage(
        `Error processing table data: ${GeneralHelpers.errorMessage(error)}`,
        'danger'
      );
      context.setChunkResultStatus("error");
      throw error; // Rethrow to allow try/catch to work
    }
  }

  private async processByteStringToCsv(data: string): Promise<unknown> {
    let csvString: string;

    if (GeneralHelpers.canBeParsedToNumberArray(data)) {
      const numberArray = data.split(',').map(Number);
      const uint8Array = new Uint8Array(numberArray);
      csvString = new TextDecoder().decode(uint8Array);
    } else {
      csvString = data; // Assume it's already a CSV string
    }

    return new Promise((resolve, reject) => {
      this.papa.parse(csvString, {
        complete: (results) => {
          if (results.errors.length > 0) {
            reject(
              new Error('CSV parsing error: ' + results.errors[0].message)
            );
          } else {
            resolve(results.data);
          }
        },
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
      });
    });
  }

  private processJsonData(data: string): unknown[] {
    const json = GeneralHelpers.jsonParse(data);
    // jsonParse returns an error message string on failure — propagate as error
    if (typeof json === 'string') {
      throw new Error(`Failed to parse JSON data: ${json}`);
    }
    return Array.isArray(json) ? json : [json];
  }

  private async processArrowData(data: string): Promise<unknown> {
    const numberArray = data.split(',').map(Number);
    const arrowBuffer = new Uint8Array(numberArray);
    const arrowTable = tableFromIPC(arrowBuffer);
    return this.processArrowTable(arrowTable);
  }

  // `arrowTable` is an Apache Arrow table handed to arquero's `fromArrow`; the
  // two libraries ship separate structural Arrow types, so this stays `any` to
  // avoid a spurious cross-library type conflict at the interop boundary.
  private async processArrowTable(arrowTable: any): Promise<unknown> {
    const arqueroTable = fromArrow(arrowTable);

    try {
      const tableJson = arqueroTable.toJSON();
      return GeneralHelpers.jsonParse(tableJson);
    } catch (error) {
      const csvData = arqueroTable.toCSV();
      console.error('Error converting Arrow table to JSON:', error);
      return this.processByteStringToCsv(csvData);
    }
  }

  private processObjectData(data: unknown): unknown[] {
    return Array.isArray(data) ? data : [data];
  }

  // `data` is dynamic tabular data (Arrow-like {schema,data}, array of rows, or
  // a single object) produced by the process* helpers, so the param/return stay
  // `any`; the structural shapes are discriminated at runtime below.
  private formatDataForTableComponent(data: any): any {
    console.log('RenderService: formatDataForTableComponent called with data:', data);
    let formattedData: Record<string, unknown>[] = [];
    let columns: { name: string; dataKey: string; isSortable: boolean }[] = [];

    if (data.schema && data.data) {
      console.log('RenderService: Processing Arrow-like format');
      // Handle Arrow-like format
      const fieldNames = (data.schema.fields ?? []).map((field: { name: string }) => field.name);
      // Guard dynamic step data: empty fields or a non-array first column would
      // otherwise throw when indexing/reading .length below.
      const firstColumn = fieldNames.length > 0 ? data.data[fieldNames[0]] : undefined;
      const rowCount = Array.isArray(firstColumn) ? firstColumn.length : 0;

      for (let i = 0; i < rowCount; i++) {
        const row: Record<string, unknown> = {};
        fieldNames.forEach((fieldName: string) => {
          row[fieldName] = data.data[fieldName]?.[i];
        });
        formattedData.push(row);
      }

      columns = fieldNames.map((fieldName: string) => ({
        name: `${fieldName} (${typeof formattedData[0]?.[fieldName]})`,
        dataKey: fieldName,
        isSortable: true,
      }));
    } else if (Array.isArray(data)) {
      console.log('RenderService: Processing array of objects');
      // Handle array of objects
      formattedData = data;
      if (formattedData.length > 0) {
        columns = Object.keys(formattedData[0]).map((key) => ({
          name: `${key} (${typeof formattedData[0][key]})`,
          dataKey: key,
          isSortable: true,
        }));
      }
    } else if (typeof data === 'object') {
      console.log('RenderService: Processing single object');
      // Handle single object
      formattedData = [data];
      columns = Object.keys(data).map((key) => ({
        name: `${key} (${typeof data[key]})`,
        dataKey: key,
        isSortable: true,
      }));
    } else {
      console.error('RenderService: Unsupported data format', data);
      return { data: [], columns: [] };
    }

    const result = {
      data: formattedData,
      columns: columns,
    };

    console.log('RenderService: formatDataForTableComponent result:', result);
    console.log('RenderService: Sample data row:', formattedData[0]);
    console.log('RenderService: Column definitions:', columns);
    return result;
  }

  public async displayImageGroup(args: any[], context: ChunkContext) {
    if (!Array.isArray(args) || args.length === 0) {
      const error = new Error('Error displaying image group');
      context.addMessage(error.message, 'danger');
      throw error;
    }
    try {
      const first = args[0];
      let imageGroup: ImageGroupResult;

      // Filename-array form: load each file via the precedence system and
      // build the ImageGroupResult shape internally. Mirrors the filename
      // support displayImage already provides for single images.
      if (
        Array.isArray(first) &&
        first.length > 0 &&
        first.every((x: unknown) => typeof x === 'string')
      ) {
        const filenames: string[] = first;
        const title = typeof args[1] === 'string' ? args[1] : undefined;
        const images: ImageResult[] = await Promise.all(
          filenames.map(async filename => {
            const fileData = await this.fileUnifiedService.getFile(
              [filename, ResponseType.ArrayBuffer],
              context,
            );
            if (!fileData) {
              throw new Error(`Could not load image file: ${filename}`);
            }
            const blob =
              fileData instanceof Blob ? fileData : new Blob([fileData]);
            const base64String = await GeneralHelpers.blobToBase64(blob);
            const ext = GeneralHelpers
              .fileExtensionFromString(filename)
              .toLowerCase();
            const subtype = ext === 'jpg' ? 'jpeg' : ext || 'png';
            return {
              kind: 'image' as const,
              src: `data:image/${subtype};base64,${base64String}`,
              caption: filename,
            };
          }),
        );
        imageGroup = {
          kind: 'imageGroup',
          images,
          ...(title ? { title } : {}),
        };
      } else if (typeof first === 'object' && first !== null) {
        imageGroup = first as ImageGroupResult;
      } else {
        imageGroup = JSON.parse(first);
      }

      if (
        !imageGroup ||
        typeof imageGroup !== 'object' ||
        imageGroup.kind !== 'imageGroup'
      ) {
        const error = new Error('Invalid image group data');
        context.addMessage(error.message, 'danger');
        throw error;
      }
      context.showImageGroup(imageGroup);
    } catch (error) {
      // Add the message but also rethrow so try/catch works
      context.addMessage(
        `Error displaying image group: ${GeneralHelpers.errorMessage(error)}`,
        'danger'
      );
      throw error; // Rethrow to allow try/catch to work
    }
  }

  public async renderImage(args: any[], context: ChunkContext) {
    if (!Array.isArray(args) || args.length === 0) {
      const error = new Error(ERROR_DISPLAY_IMAGE);
      context.addMessage(ERROR_DISPLAY_IMAGE, 'danger');
      throw error;
    }

    let image = args[0];
    const chunkData = context.getChunk();

    if (!chunkData?.stepId) {
      const error = new Error(ERROR_DISPLAY_IMAGE);
      context.addMessage(ERROR_DISPLAY_IMAGE, 'danger');
      throw error;
    }

    try {
      // If image is a regular array of numbers (from JavaScript context dump), convert to Uint8Array
      if (Array.isArray(image) && image.length > 0 && typeof image[0] === 'number') {
        image = new Uint8Array(image);
      }
      // If image is an ArrayBuffer (from QuickJS getArrayBuffer), wrap in Uint8Array
      if (image instanceof ArrayBuffer) {
        image = new Uint8Array(image);
      }
      // If image is a Blob or Uint8Array, it's already processed data from getFile()
      if (image instanceof Blob || image instanceof Uint8Array || image instanceof Uint16Array || image instanceof Uint32Array) {
        // Image is already processed, continue to display logic
      }
      // If image is a string that doesn't look like base64 or URL, try to load it as a file
      else if (
        typeof image === 'string' &&
        !image.startsWith('data:image/') &&
        !image.startsWith('http') &&
        !GeneralHelpers.canBeParsedToNumberArray(image) &&
        image !== '[object Blob]'  // Don't try to load if it's a serialized Blob
      ) {
        // Try to load the file
        // context.setBusy(true, 'Loading image file');
        try {
          const fileData = await this.fileUnifiedService.getFile([image, ResponseType.ArrayBuffer], context);
          if (fileData) {
            image = fileData;
          } else {
            const errorMsg = `Could not load image file: ${image}`;
            context.addMessage(errorMsg, 'danger');
            throw new Error(errorMsg);
          }
        } finally {
          // context.setBusy(false);
        }
      }

      if (GeneralHelpers.isValidImageSource(image)) {
        try {
          const base64Data = await GeneralHelpers.convertToBase64(image);
          this.addToRenderList({
            value: `${GeneralHelpers.getBase64MimeType(FILE_EXTENSIONS.png)}${base64Data}`,
            stepId: chunkData.stepId,
            type: 'image',
          });
        } catch (error) {
          context.setChunkResultStatus("error");
          const errorMsg = `Error processing image data: ${GeneralHelpers.errorMessage(error)}`;
          context.addMessage(errorMsg, 'danger');
          throw new Error(errorMsg);
        }
      } else if (image instanceof Blob) {
        // Handle Blob objects directly
        try {
          const base64String = await GeneralHelpers.blobToBase64(image);
          const mimeType = image.type || 'image/jpeg';
          const base64Data = `data:${mimeType};base64,${base64String}`;
          this.addToRenderList({
            value: base64Data,
            stepId: chunkData.stepId,
            type: 'image',
          });
        } catch (error) {
          context.setChunkResultStatus("error");
          const errorMsg = `Error processing Blob image: ${GeneralHelpers.errorMessage(error)}`;
          context.addMessage(errorMsg, 'danger');
          throw new Error(errorMsg);
        }
      } else if (image instanceof Uint8Array || image instanceof Uint16Array || image instanceof Uint32Array) {
        // Handle typed arrays (from getFile conversion)
        try {
          // Convert typed array to regular Uint8Array to avoid TypeScript issues
          const uint8Array = new Uint8Array(image);
          const imageBlob = new Blob([uint8Array], { type: 'image/jpeg' });
          const base64String = await GeneralHelpers.blobToBase64(imageBlob);
          const base64Data = `data:image/jpeg;base64,${base64String}`;
          this.addToRenderList({
            value: base64Data,
            stepId: chunkData.stepId,
            type: 'image',
          });
        } catch (error) {
          context.setChunkResultStatus("error");
          const errorMsg = `Error processing typed array image: ${GeneralHelpers.errorMessage(error)}`;
          context.addMessage(errorMsg, 'danger');
          throw new Error(errorMsg);
        }
      } else if (typeof image === 'string' && GeneralHelpers.canBeParsedToNumberArray(image)) {
        try {
          const numberArray = image.split(',').map(Number);
          const imageBuffer = new Uint8Array(numberArray);
          const imageBlob = new Blob([imageBuffer], { type: 'image/png' });
          const imageUrl = URL.createObjectURL(imageBlob);
          this.addToRenderList({
            value: imageUrl,
            stepId: chunkData.stepId,
            type: 'image',
          });
        } catch (error) {
          context.setChunkResultStatus("error");
          const errorMsg = `Error processing byte array image: ${GeneralHelpers.errorMessage(error)}`;
          context.addMessage(errorMsg, 'danger');
          throw new Error(errorMsg);
        }
      } else if (typeof image === 'string' && (image.startsWith('data:image/') || image.startsWith('http'))) {
        // Handle base64 images and URLs
        this.addToRenderList({
          value: image,
          stepId: chunkData.stepId,
          type: 'image',
        });
      } else if (typeof image === 'string' && image === '[object Blob]') {
        // Handle serialized Blob from JavaScript context
        const errorMsg = 'Cannot display Blob object directly. Please use ehq.getFile() to load the file first, then pass the result to ehq.displayImage().';
        context.addMessage(errorMsg, 'danger');
        context.setChunkResultStatus("error");
        throw new Error(errorMsg);
      } else {
        const errorMsg = 'Invalid image data: must be a byte array, base64 string, or URL';
        context.addMessage(errorMsg, 'danger');
        context.setChunkResultStatus("error");
        throw new Error(errorMsg);
      }
    } catch (error) {
      // Log to Messages tab but also throw so try/catch works
      context.addMessage(`Error displaying image: ${GeneralHelpers.errorMessage(error)}`, 'danger');
      context.setChunkResultStatus("error");
      throw error;
    }
  }

  /**
   * Render a LOCAL workflow/cache markdown file in an output tab (SCRUM-712).
   * `args[0]` = file name; `args[1]` = optional `{ title?, display?: 'tab' | 'window' }`.
   * Remote references are rejected outright; the content is sanitized (no JS, no
   * remote loads) by {@link MarkdownService.markdownToSafeHtml} before display.
   */
  public async renderMarkdown(
    args: any[],
    context: ChunkContext,
    source?: string,
  ) {
    if (!Array.isArray(args) || args.length === 0) {
      context.addMessage(ERROR_DISPLAY_MARKDOWN, 'danger');
      throw new Error(ERROR_DISPLAY_MARKDOWN);
    }

    const fileName = args[0];
    const options =
      args.length > 1 &&
      args[1] !== null &&
      typeof args[1] === 'object' &&
      !Array.isArray(args[1])
        ? args[1]
        : {};
    const chunkData = context.getChunk();

    if (!chunkData?.stepId) {
      context.addMessage(ERROR_DISPLAY_MARKDOWN, 'danger');
      throw new Error(ERROR_DISPLAY_MARKDOWN);
    }

    if (typeof fileName !== 'string' || !fileName.trim()) {
      const msg = 'displayMarkdown requires a local markdown file name.';
      context.addMessage(msg, 'danger');
      context.setChunkResultStatus('error');
      throw new Error(msg);
    }

    // Local files only — reject remote references (http(s):, other schemes,
    // protocol-relative //host) before doing any work.
    if (/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(fileName.trim())) {
      const msg = `displayMarkdown only accepts local workflow files, not remote references: ${fileName}`;
      context.addMessage(msg, 'danger');
      context.setChunkResultStatus('error');
      throw new Error(msg);
    }

    try {
      const fileContent = await this.fileUnifiedService.getFile(
        [fileName, ResponseType.Text],
        context,
        source,
      );
      const src = await this.coerceToText(fileContent);
      const safeHtml = await this.markdownService.markdownToSafeHtml(
        src,
        chunkData.stepId,
        source,
      );
      const display = options.display === 'window' ? 'window' : 'tab';

      this.addToRenderList({
        value: safeHtml,
        stepId: chunkData.stepId,
        type: 'markdown',
        name: options.title,
        options: { display, title: options.title },
      });
    } catch (error) {
      context.addMessage(`Error displaying markdown: ${GeneralHelpers.errorMessage(error)}`, 'danger');
      context.setChunkResultStatus('error');
      throw error;
    }
  }

  /** Normalize whatever getFile returns for a text/markdown file into a string. */
  private async coerceToText(content: unknown): Promise<string> {
    if (content === null || content === undefined) {
      return '';
    }
    if (typeof content === 'string') {
      return content;
    }
    if (content instanceof Uint8Array) {
      return new TextDecoder().decode(content);
    }
    if (content instanceof ArrayBuffer) {
      return new TextDecoder().decode(new Uint8Array(content));
    }
    if (content instanceof Blob) {
      return await content.text();
    }
    if (
      Array.isArray(content) &&
      content.every((n) => typeof n === 'number')
    ) {
      return new TextDecoder().decode(new Uint8Array(content as number[]));
    }
    return String(content);
  }

  public skipToStep(data: any[], context: ChunkContext) {
    try {
      const stepName = data[0];
      if (!stepName) {
        const error = new Error('skipToStep requires a step name');
        context.addMessage(error.message, 'danger');
        throw error;
      }

      // Find the step by name
      let step = this.stepService.getStepByName(stepName);

      // Fallback: partial name match
      if (step === null) {
        step = this.stepService.stepList.find((s) =>
          s.name.toLowerCase().includes(stepName.toLowerCase())
        ) || null;
      }

      if (step === null) {
        const error = new Error(`skipToStep: step "${stepName}" not found. Available steps: ${this.stepService.stepList.map((s) => s.name).join(', ')}`);
        context.addMessage(error.message, 'danger');
        throw error;
      }

      // In browser, scroll to the target step (steps run individually, no multi-step skip)
      this.messageService.sendMessage(SCROLL_TO_CHUNK, step);
      context.addLog(`skipToStep("${stepName}") — scrolled to step (browser runs steps individually)`, 'info');
    } catch (error) {
      const message = GeneralHelpers.errorMessage(error);
      if (!message.includes('not found') && !message.includes('requires')) {
        context.addMessage(`Error in skipToStep: ${message}`, 'danger');
      }
      throw error;
    }
  }

  public log(
    data: any[],
    context: ChunkContext,
  ) {
    try {
      if (!Array.isArray(data)) {
        const error = new Error('Invalid log data: must be an array');
        context.addMessage(error.message, 'danger');
        throw error;
      }

      const message = typeof data[0] === 'boolean' ? data[0].toString() : data[0];
      const severity = data[1];

      context.addLog(message, severity);
    } catch (error) {
      context.addMessage(`Error logging data: ${GeneralHelpers.errorMessage(error)}`, 'danger');
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }

  public output(
    data: any[],
    context: ChunkContext,
    typeRunner: 'javascript' | 'python'
  ) {
    try {
      if (!Array.isArray(data)) {
        const error = new Error('Invalid output data: must be an array');
        context.addMessage(error.message, 'danger');
        throw error;
      }
      context.addOutput(data[0], data[1], typeRunner);
    } catch (error) {
      context.addMessage(`Error adding output: ${GeneralHelpers.errorMessage(error)}`, 'danger');
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }

  /**
   * Test method to debug table display issues
   */
  public testTableDisplay(context: ChunkContext): void {
    console.log('RenderService: testTableDisplay called');

    // Create test data
    const testData = [
      { id: 1, name: 'John Doe', age: 30, city: 'New York' },
      { id: 2, name: 'Jane Smith', age: 25, city: 'Los Angeles' },
      { id: 3, name: 'Bob Johnson', age: 35, city: 'Chicago' }
    ];

    console.log('RenderService: Test data created:', testData);

    // Call renderTable with test data
    this.renderTable([testData], context);
  }


  /**
     * Renders a chart visualization
     * @param args Array of arguments from Python, where args[0] is the chart data
     * @param context The chunk context
     */
  public renderChart(args: any[], context: ChunkContext): void {
    if (!Array.isArray(args) || args.length === 0) {
      const error = new Error('Error displaying chart: No data provided');
      context.addMessage(error.message, 'danger');
      throw error;
    }

    const chartData = args[0];
    const chunkData = context.getChunk();

    if (!chunkData?.stepId) {
      const error = new Error('Error displaying chart: Invalid chunk context');
      context.addMessage(error.message, 'danger');
      throw error;
    }

    try {
      // Mark the chunk as processing to prevent race conditions
      // context.setBusy(true, 'Preparing chart visualization');

      // Add to render list to trigger the component flow
      this.addToRenderList({
        value: chartData,
        stepId: chunkData.stepId,
        type: 'chart',
        name: 'Chart' // Or extract a more specific name from the data if available
      });
    } catch (error) {
      context.setChunkResultStatus("error");
      const errorMsg = `Error processing chart data: ${GeneralHelpers.errorMessage(error)}`;
      context.addMessage(errorMsg, 'danger');
      throw error; // Rethrow to allow try/catch blocks to work
    } finally {
      // context.setBusy(false);
    }
  }
}
